import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaSpinner, FaSearch } from 'react-icons/fa';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import { formatPlaceAddress, stripPlusCode } from '../utils/locationMatcher';

const POPULAR_CITIES_IN = [
  { name: 'Mumbai', description: 'Maharashtra' },
  { name: 'Delhi', description: 'India' },
  { name: 'Bangalore', description: 'Karnataka' },
  { name: 'Pune', description: 'Maharashtra' },
  { name: 'Hyderabad', description: 'Telangana' },
  { name: 'Kolkata', description: 'West Bengal' },
  { name: 'Goa', description: 'Goa' },
];

const LocationInput = forwardRef(({
  value,
  onChange,
  placeholder = 'Enter location',
  label,
  className = '',
  iconClassName = '',
  onPlaceSelected = null,
  country = 'in',
  types = [],
  required = false,
  hideHeader = false,
  hideDropdown = false,
  onSuggestions = null
}, ref) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 280 });

  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const suggestionsTimeoutRef = useRef(null);

  const { isLoaded, autocompleteService, placesService } = useGoogleMaps();

  // Load recent searches
  useEffect(() => {
    const stored = localStorage.getItem('recentLocationSearches');
    if (stored) {
      try { setRecentSearches(JSON.parse(stored)); } catch (e) { /* ignore */ }
    }
  }, []);

  const saveRecentSearch = useCallback((location) => {
    setRecentSearches((prev) => {
      const locationAddress = stripPlusCode(location.description || location.name || '');
      if (!locationAddress) return prev;
      const filtered = prev.filter((item) => {
        const itemAddr = stripPlusCode(item.description || item.name || '');
        return itemAddr.toLowerCase() !== locationAddress.toLowerCase();
      });
      const updated = [
        {
          name: locationAddress,
          description: locationAddress,
          place_id: location.place_id || null,
          location: location.location || null
        },
        ...filtered
      ].slice(0, 5);
      try {
        localStorage.setItem('recentLocationSearches', JSON.stringify(updated));
      } catch (e) { /* ignore */ }
      return updated;
    });
  }, []);

  useEffect(() => {
    if (isLoaded && autocompleteService) autocompleteServiceRef.current = autocompleteService;
    if (isLoaded && placesService) placesServiceRef.current = placesService;
  }, [isLoaded, autocompleteService, placesService]);

  const memoizedTypes = useMemo(() => types, [JSON.stringify(types)]);

  // Compute dropdown position using fixed positioning (escapes all overflow:hidden ancestors)
  const updateDropdownPos = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const minWidth = 350; // MakeMyTrip style slightly wider dropdown
    const availableWidth = typeof window !== 'undefined' ? window.innerWidth - 24 : 350;
    const width = Math.min(Math.max(rect.width, minWidth), availableWidth);

    let left = rect.left + (typeof window !== 'undefined' ? window.scrollX : 0);
    if (typeof window !== 'undefined' && rect.left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12 + window.scrollX);
    }

    setDropdownPos({
      top: rect.bottom + (typeof window !== 'undefined' ? window.scrollY : 0),
      left,
      width,
    });
  }, []);

  useEffect(() => {
    if (showSuggestions && !hideDropdown) {
      updateDropdownPos();
      window.addEventListener('resize', updateDropdownPos);
      window.addEventListener('scroll', updateDropdownPos, true);
      return () => {
        window.removeEventListener('resize', updateDropdownPos);
        window.removeEventListener('scroll', updateDropdownPos, true);
      };
    }
  }, [showSuggestions, updateDropdownPos, hideDropdown]);

  // Fetch autocomplete suggestions
  const fetchSuggestions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      setSuggestions([]);
      if (onSuggestions) onSuggestions([]);
      return;
    }
    if (!autocompleteServiceRef.current) return;
    try {
      setIsLoading(true);
      const request = {
        input: inputValue,
        componentRestrictions: { country: [country] },
        language: 'en',
      };
      if (memoizedTypes && memoizedTypes.length > 0) request.types = memoizedTypes;

      const predictions = await new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(request, (preds, status) => {
          resolve(status === 'OK' && preds ? preds : []);
        });
      });
      setSuggestions(predictions || []);
      if (onSuggestions) onSuggestions(predictions || []);
      setSelectedIndex(-1);
    } catch {
      setSuggestions([]);
      if (onSuggestions) onSuggestions([]);
    }
    finally { setIsLoading(false); }
  }, [country, memoizedTypes, onSuggestions]);

  useEffect(() => {
    if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current);
    if (value && value.length >= 2 && showSuggestions) {
      suggestionsTimeoutRef.current = setTimeout(() => fetchSuggestions(value), 300);
    } else {
      setSuggestions([]);
      if (onSuggestions) onSuggestions([]);
    }
    return () => { if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current); };
  }, [value, showSuggestions, fetchSuggestions, onSuggestions]);

  const handleSelectSuggestion = useCallback((suggestion) => {
    if (!suggestion.place_id) {
      // For popular cities / recent searches (no place_id), use the clean city name
      const rawName = suggestion.name || suggestion.description;
      const displayName = stripPlusCode(rawName);
      onChange(displayName);
      setShowSuggestions(false);
      setSuggestions([]);
      if (onSuggestions) onSuggestions([]);
      saveRecentSearch({ name: displayName, description: suggestion.description || '' });
      if (onPlaceSelected) onPlaceSelected({ name: displayName, formatted_address: displayName });
      return;
    }
    if (!placesServiceRef.current) {
      const displayName = formatPlaceAddress(null, suggestion);
      onChange(displayName);
      setShowSuggestions(false);
      if (onSuggestions) onSuggestions([]);
      saveRecentSearch({ name: suggestion.structured_formatting?.main_text || displayName, description: displayName });
      if (onPlaceSelected) onPlaceSelected({ name: suggestion.structured_formatting?.main_text || displayName, formatted_address: displayName });
      return;
    }
    placesServiceRef.current.getDetails(
      { placeId: suggestion.place_id, fields: ['formatted_address', 'geometry', 'name', 'place_id', 'address_components', 'types'] },
      (place, status) => {
        if (status === 'OK' && place) {
          const displayName = formatPlaceAddress(place, suggestion);
          const placeName = place.name || suggestion.structured_formatting?.main_text || displayName.split(',')[0].trim();
          const normalizedPlace = {
            ...place,
            name: placeName,
            formatted_address: displayName,
          };
          onChange(displayName);
          setShowSuggestions(false);
          setSuggestions([]);
          if (onSuggestions) onSuggestions([]);
          saveRecentSearch({ name: placeName, description: displayName });
          if (onPlaceSelected) onPlaceSelected(normalizedPlace);
        } else {
          const fallbackName = formatPlaceAddress(null, suggestion);
          onChange(fallbackName);
          setShowSuggestions(false);
          if (onSuggestions) onSuggestions([]);
          saveRecentSearch({ name: suggestion.structured_formatting?.main_text || fallbackName, description: fallbackName });
          if (onPlaceSelected) onPlaceSelected({ name: suggestion.structured_formatting?.main_text || fallbackName, formatted_address: fallbackName });
        }
      }
    );
  }, [onChange, onPlaceSelected, saveRecentSearch, onSuggestions]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    selectSuggestion: (suggestion) => {
      handleSelectSuggestion(suggestion);
    },
    focus: () => {
      inputRef.current?.focus();
    }
  }), [handleSelectSuggestion]);

  // Click outside: close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const clickedInDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!clickedInWrapper && !clickedInDropdown) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const allItems = suggestions.length > 0
    ? suggestions
    : ((!value || value.length < 2) ? POPULAR_CITIES_IN : []);

  const handleKeyDown = (e) => {
    if (!showSuggestions || allItems.length === 0) return;
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setSelectedIndex(p => (p + 1) % allItems.length); break;
      case 'ArrowUp': e.preventDefault(); setSelectedIndex(p => (p - 1 < 0 ? allItems.length - 1 : p - 1)); break;
      case 'Enter': e.preventDefault(); if (selectedIndex >= 0) handleSelectSuggestion(allItems[selectedIndex]); break;
      case 'Escape': setShowSuggestions(false); break;
      default: break;
    }
  };

  const clearInput = () => {
    onChange('');
    setShowSuggestions(true);
    setSuggestions([]);
    if (onSuggestions) onSuggestions([]);
    inputRef.current?.focus();
  };

  const showPopularCities = (!value || value.length < 2) && suggestions.length === 0;
  const showRecentSearches = (!value || value.length < 2) && recentSearches.length > 0 && suggestions.length === 0;

  // The dropdown rendered via portal (fixed position, escapes all overflow clipping)
  const dropdownMarkup = (showSuggestions && !hideDropdown) ? createPortal(
    <div
      ref={dropdownRef}
      className="location-input-dropdown"
      style={{
        position: 'absolute',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 40,
      }}
    >
      {/* API Suggestions */}
      {suggestions.length > 0 && (
        <div>
          <div className="location-input-sectionLabel">SUGGESTIONS</div>
          {suggestions.map((s, idx) => {
            const fullAddressText = stripPlusCode(
              s.description || 
              (s.structured_formatting ? `${s.structured_formatting.main_text}, ${s.structured_formatting.secondary_text}` : s.name)
            );
            return (
              <div
                key={s.place_id || idx}
                onMouseDown={() => handleSelectSuggestion(s)}
                className={`location-input-cityItem ${idx === selectedIndex ? 'location-input-cityItem--active' : ''}`}
              >
                {fullAddressText}
              </div>
            );
          })}
        </div>
      )}

      {/* Recent Searches */}
      {showRecentSearches && (
        <div>
          <div className="location-input-sectionLabel">RECENT SEARCHES</div>
          {recentSearches.map((s, idx) => {
            const recentAddressText = stripPlusCode(s.description || s.name || s.address || '');
            return (
              <div
                key={`recent-${idx}`}
                onMouseDown={() => handleSelectSuggestion(s)}
                className={`location-input-cityItem ${idx === selectedIndex ? 'location-input-cityItem--active' : ''}`}
              >
                {recentAddressText}
              </div>
            );
          })}
        </div>
      )}

      {/* Popular Cities */}
      {showPopularCities && (
        <div>
          <div className="location-input-sectionLabel">POPULAR CITIES</div>
          {POPULAR_CITIES_IN.map((city, idx) => (
            <div
              key={`city-${idx}`}
              onMouseDown={() => handleSelectSuggestion(city)}
              className={`location-input-cityItem ${(!showRecentSearches && idx === selectedIndex) ? 'location-input-cityItem--active' : ''}`}
            >
              {city.name}
            </div>
          ))}
        </div>
      )}
    </div>,
    document.body
  ) : null;

  if (!isLoaded) {
    return (
      <div ref={wrapperRef} className={`location-input-mmtStyle ${className}`}>
        {!hideHeader && (
          <div className="location-input-header"><span>{label ? label.toUpperCase() : 'FROM'}</span></div>
        )}
        <div className="location-input-searchRow">
          <FaSearch className="location-input-searchIcon" />
          <input
            type="text" value={value} className="location-input-field"
            placeholder="Loading maps..." disabled required={required}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={`location-input-mmtStyle ${className}`}>
      {!hideHeader && (
        <div className="location-input-header"><span>{label ? label.toUpperCase() : 'FROM'}</span></div>
      )}
      {/* Search row */}
      <div className="location-input-searchRow">
        <FaSearch className="location-input-searchIcon" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { setShowSuggestions(true); if (!hideDropdown) updateDropdownPos(); }}
          className="location-input-field"
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          spellCheck="false"
        />
        {isLoading && <FaSpinner className="location-input-spinner" />}
        {!isLoading && value && (
          <button type="button" onMouseDown={clearInput} className="location-input-clearBtn" aria-label="Clear">
            <FaTimes />
          </button>
        )}
      </div>

      {/* Portal dropdown */}
      {dropdownMarkup}
    </div>
  );
});

export default LocationInput;
